<template>
    <section class="py-16 lg:py-[150px] bg-white" :style="{ backgroundColor }">
        <div class="px-5 lg:px-[120px]">
            <h2 class="text-[28px] md:text-[44px] lg:text-[44px] lg:leading-[57px] font-semibold mb-[50px]" :class="[titleColor, titleAlignment]">
                <template v-if="titleHighlight">
                    <span v-html="renderTitle()"></span>
                </template>
                <template v-else>
                    {{ title }}
                </template>
            </h2>
            <div v-if="paragraphs" class="prose prose-lg max-w-none text-black space-y-6 w-full font-pro text-[18px]" :class="paragraphsLength">
                <p v-html="paragraphs" />
            </div>
            <div v-if="tiles.length" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-[50px]">
                <div v-for="(tile, index) in tiles" :key="index" :class="tilesBackground" class="rounded-lg p-5 text-center">
                    <h3 class="text-[44px] font-semibold mb-2" :class="tilesTitleColor" v-html="tile.title" />
                    <p :class="tilesDescriptionColor" class="text-[20px] font-semibold" v-html="tile.description"></p>
                </div>
            </div>
            <div v-if="ctaText" class="text-center mt-10">
                <router-link
                    v-if="showCta"
                    :to="ctaLink"
                    class="inline-flex w-full justify-center lg:w-fit items-center gap-2 text-white px-4 py-4 rounded-full font-semibold transition hover:opacity-90"
                    :style="{ background: `linear-gradient(to bottom right, ${buttonGradientFrom}, ${buttonGradientTo})` }"
                >
                    {{ ctaText }}
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                </router-link>
                <div v-else class="text-white px-2 lg:px-6 py-3 rounded-full font-semibold transition text-xs lg:text-md" :style="{ backgroundColor: fallbackCtaBackgroundColor }">
                    {{ fallbackCtaText }}
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const props = defineProps({
    backgroundColor: {
        type: String,
        default: '#FFFFFF'
    },
    title: {
        type: String,
        default: ''
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightGradientFrom: {
        type: String,
        default: '#11D2D4'
    },
    titleHighlightGradientTo: {
        type: String,
        default: '#0C7475'
    },
    titleColor: {
        type: String,
        default: 'text-blue-dark'
    },
    titleAlignment: {
        type: String,
        default: 'text-left'
    },
    paragraphs: {
        type: String,
        default: ''
    },
    paragraphsLength: {
        type: String,
        default: 'max-w-3xl'
    },
    tiles: {
        type: Array,
        default: () => []
    },
    tilesTitleColor: {
        type: String,
        default: 'text-blue-dark'
    },
    tilesDescriptionColor: {
        type: String,
        default: 'text-black'
    },
    tilesBackground: {
        type: String,
        default: 'bg-white'
    },
    ctaText: {
        type: String,
        default: ''
    },
    ctaLink: {
        type: String,
        default: '/'
    },
    showCta: {
        type: Boolean,
        default: true
    },
    buttonGradientFrom: {
        type: String,
        default: '#11D2D4'
    },
    buttonGradientTo: {
        type: String,
        default: '#0C7475'
    },
    fallbackCtaText: {
        type: String,
        default: ''
    },
    fallbackCtaBackgroundColor: {
        type: String,
        default: '#1E40AF'
    },
    image: {
        type: String,
        default: ''
    }
});

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const parts = props.title.split(props.titleHighlight);

    if (parts.length === 1) return props.title;

    return parts
        .map((part, index) => {
            if (index === 0) {
                return part;
            }

            return `<span class="bg-gradient-to-r text-transparent bg-clip-text" style="background: linear-gradient(to right, ${props.titleHighlightGradientFrom}, ${props.titleHighlightGradientTo}); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">${props.titleHighlight}</span>${part}`;
        })
        .join('');
};
</script>

<style scoped>
.prose {
    max-width: none;
}

.prose p {
    margin-bottom: 1rem;
}
</style>
